import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';
import { LegendFooter } from '../published/LegendFooter';
import { PublishedTimeline } from '../published/PublishedTimeline';
import { useExportTimeline } from './useExportTimeline';

export const internalTimelineExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  legend: z.coerce.string().optional(),
  vision: z.string().optional(),
});

export type InternalTimelineExportSearchParams = z.infer<typeof internalTimelineExportSearchSchema>;

function getBooleanQueryParam(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return value === '1';
}

export function ExportTimelinePdfPage({
  viewId,
  visualizationId,
  searchParams,
}: {
  viewId: number;
  visualizationId: number;
  searchParams: InternalTimelineExportSearchParams;
}) {
  const { t } = useTranslation();
  const query = useExportTimeline({
    viewId,
    visualizationId,
    token: searchParams.token,
  });

  const title = query.data?.metadata.title;
  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  if (query.isPending) {
    return (
      <output className="flex items-center justify-center h-screen text-xl">
        {t(($) => $.views.published.loading)}
      </output>
    );
  }

  if (query.isError) {
    return (
      <div
        className="flex items-center justify-center h-screen text-xl text-text-error"
        role="alert"
      >
        {t(($) => $.views.published.errorLoading, { message: query.error.message })}
      </div>
    );
  }

  const { metadata } = query.data;
  const legendItems = query.data.legendItems ?? [];

  const showTitle = getBooleanQueryParam(searchParams.title, true);
  const showTempoBranding = getBooleanQueryParam(searchParams.branding, true);
  const showPublishDate = getBooleanQueryParam(searchParams.date, true);
  const showLegend = getBooleanQueryParam(searchParams.legend, true);
  const visionStatement = searchParams.vision?.trim() ?? '';
  const showVisionStatement = visionStatement.length > 0;

  const showHeader = showTitle || showVisionStatement;

  const publishDateValue = metadata.publishedAt || metadata.createdAt;
  const publishDate = publishDateValue
    ? formatLocalDate(publishDateValue, DateFormat.MONTH_LONG_DAY_SHORT_YEAR)
    : '';

  return (
    <div
      data-publicationid="timeline-main-container"
      className="bg-bg-surface-base w-full h-full flex flex-col"
    >
      {showHeader && (
        <div className="px-8 pt-6 pb-4 shrink-0" data-export-target="header">
          {showTitle && (
            <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">{metadata.title}</h1>
          )}
          {showVisionStatement && (
            <div className="max-h-32 overflow-y-auto">
              <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                {visionStatement}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 w-full">
        <PublishedTimeline
          data={query.data}
          searchParams={{}}
          showLegend={false}
          hideLegendOverlay={true}
        />
      </div>

      {(showPublishDate || showTempoBranding || showLegend) && (
        <div className="py-4 w-full shrink-0" data-export-target="footer">
          {showLegend && legendItems.length > 0 && (
            <nav
              aria-label={t(($) => $.views.export.timelineLegendAriaLabel)}
              className="w-full mb-3"
              data-component="legend-wrapper"
            >
              <LegendFooter items={legendItems} />
            </nav>
          )}
          <div className="flex items-center justify-between px-8">
            <div className="text-sm text-text-neutral-secondary">
              {showPublishDate && publishDate && (
                <time dateTime={publishDateValue}>
                  {t(($) => $.views.published.publishedOn, { date: publishDate })}
                </time>
              )}
            </div>
            <div className="flex items-center gap-3">
              {showTempoBranding && (
                <div className="flex items-center gap-2">
                  <img
                    src={TempoLogo}
                    alt={t(($) => $.views.export.tempoBrandingAlt)}
                    className="h-6 w-6"
                  />
                  <span className="text-sm font-semibold text-text-neutral-primary">
                    {t(($) => $.views.export.tempoBrandingName)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
